import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { AddinContextService } from '@service/addin-context.service';
import {
  AuthConfig,
  DateTimeProvider,
  HashHandler,
  OAuthErrorEvent,
  OAuthLogger,
  OAuthService,
  OAuthStorage,
  UrlHelperService,
  ValidationHandler,
} from 'angular-oauth2-oidc';

import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
/**
 * Represents a service for handling OAuth authentication in an add-in.
 * Extends the base OAuthService class.
 */
export class AddinOauthService extends OAuthService {
  private addinConfig = inject(AddinContextService);
  private loggerService = inject(LoggerService);

  constructor() {
    const ngZone = inject(NgZone);
    const http = inject(HttpClient);
    const storage = inject(OAuthStorage, { optional: true })!;
    const tokenValidationHandler = inject(ValidationHandler, { optional: true })!;
    const config = inject(AuthConfig, { optional: true })!;
    const urlHelper = inject(UrlHelperService);
    const logger = inject(OAuthLogger);
    const crypto = inject(HashHandler, { optional: true })!;
    const document = inject<Document>(DOCUMENT);
    const dateTimeService = inject(DateTimeProvider);

    super(ngZone, http, storage, tokenValidationHandler, config, urlHelper, logger, crypto, document, dateTimeService);
  }

  /**
   * The whole point to extending this class is to override the logic around how a popup window is opened.
   *  When running inside Office, the dialog API has to be used.
   * @param options
   * @returns
   */
  public override initLoginFlowInPopup(options?: { height?: number; width?: number; windowRef?: Window; promptBeforeOpen?: boolean }) {
    if (!this.addinConfig.isOfficeActive) {
      this.loggerService.debug('[AddinOauthService] Using normal OAuth implementation, without Office JS');
      return super.initLoginFlowInPopup(options);
    }

    options = options || {
      height: 40,
      width: 20,
      promptBeforeOpen: false,
    };

    return this.createLoginUrl(undefined, undefined, this.silentRefreshRedirectUri, false, {
      display: 'popup',
    }).then((url) => {
      return new Promise((resolve, reject) => {
        let dialog: Office.Dialog | null = null;
        const cleanup = () => {
          if (dialog !== null) dialog.close();
          dialog = null;
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tryLogin = (args: any): void => {
          if (args.error) {
            reject(`Unknown failure : # ${args.error}`);
            return;
          }
          this.tryLogin({
            customHashFragment: args.message,
            preventClearHashAfterLogin: true,
            customRedirectUri: this.silentRefreshRedirectUri,
          }).then(
            () => {
              cleanup();
              resolve(true);
            },
            (err) => {
              cleanup();
              reject(err);
            }
          );
        };

        this.loggerService.debug('[AddinOauthService] Opening dialog with URL:', url);
        Office.context.ui.displayDialogAsync(url, options, (res) => {
          this.loggerService.debug('[AddinOauthService] And the response from callback', res);

          if (res.status === Office.AsyncResultStatus.Failed) {
            this.loggerService.debug(`[AddinOauth] Could not open login page ${res.error.code}: ${res.error.message}`);
            reject(new OAuthErrorEvent('popup_blocked', {}));
          } else {
            dialog = res.value;
            this.loggerService.debug('[AddinOauthService] Page opened');
            this.loggerService.debug('[AddinOauthService] Dialog is ', dialog);
            dialog.addEventHandler(Office.EventType.DialogMessageReceived, tryLogin);
            dialog.addEventHandler(Office.EventType.DialogEventReceived, tryLogin);
          }
        });
        this.loggerService.debug('[AddinOauthService] completed open dialog async');
      });
    });
  }
}
